import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import SmartLink from "@/components/public/SmartLink";
import { Search, Truck, Store, ShieldCheck, ArrowRight, MapPin, Sparkles, Clock, CheckCircle2, ShoppingBag } from "lucide-react";

export const ICONS = { Search, Truck, Store, ShieldCheck, MapPin, Sparkles, Clock, ShoppingBag };
const HOW_ICONS = [MapPin, ShoppingBag, Truck];

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08, ease: "easeOut" } }),
};

export function SectionHead({ overline, heading, sub, center, dark }) {
  return (
    <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
      {overline && <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-orange">{overline}</p>}
      <h2 className={`mt-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-[44px] lg:leading-[1.1] ${dark ? "text-white" : "text-slate-900"}`}>{heading}</h2>
      {sub && <p className={`mt-4 text-base md:text-lg ${dark ? "text-white/70" : "text-slate-500"}`}>{sub}</p>}
    </div>
  );
}

export function Hero({ c }) {
  const h = c.hero || {};
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const go = (e) => { e.preventDefault(); navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search"); };
  return (
    <section className="hero-glow relative -mt-[72px] overflow-hidden pt-[72px] text-white" data-testid="landing-hero">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-16 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:py-28">
        <div className="lg:col-span-6">
          <motion.span initial="hidden" animate="show" variants={fade} custom={0}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/80 backdrop-blur" data-testid="hero-badge">
            <Sparkles className="h-3.5 w-3.5 text-brand-orange" /> {h.badge}
          </motion.span>
          <motion.h1 initial="hidden" animate="show" variants={fade} custom={1} data-testid="hero-title"
            className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            {h.title}<br /><span className="text-brand-orange">{h.title_accent}</span>
          </motion.h1>
          <motion.p initial="hidden" animate="show" variants={fade} custom={2} className="mt-6 max-w-lg text-base leading-relaxed text-white/70 md:text-lg" data-testid="hero-subtitle">{h.subtitle}</motion.p>
          <motion.form initial="hidden" animate="show" variants={fade} custom={3} onSubmit={go}
            className="mt-9 flex max-w-xl flex-col gap-2 rounded-3xl bg-white p-2 shadow-2xl shadow-black/30 sm:flex-row sm:rounded-full">
            <div className="flex flex-1 items-center gap-2 px-4">
              <MapPin className="h-5 w-5 shrink-0 text-brand" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={h.search_placeholder || "Enter your town or postcode"}
                className="h-12 w-full bg-transparent text-base text-slate-900 outline-none placeholder:text-slate-400" data-testid="hero-postcode-input" />
            </div>
            <button type="submit" className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-brand px-7 text-base font-bold text-white transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-brand-hover" data-testid="hero-find-btn">
              <Search className="h-4 w-4" /> {h.search_btn || h.primary_btn}
            </button>
          </motion.form>
          <motion.div initial="hidden" animate="show" variants={fade} custom={4} className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/70">
            {(h.highlights || []).map((t) => (
              <span key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand-orange" /> {t}</span>
            ))}
            {h.secondary_btn && (
              <SmartLink href={h.secondary_href || "/register"} className="group flex items-center gap-1.5 font-bold text-white" data-testid="hero-partner-btn">
                {h.secondary_btn} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </SmartLink>
            )}
          </motion.div>
        </div>
        <HeroVisual h={h} />
      </div>
    </section>
  );
}

function HeroVisual({ h }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }} className="relative lg:col-span-6">
      <div className="absolute -inset-6 rounded-[40px] bg-brand/20 blur-3xl" />
      <div className="relative overflow-hidden rounded-[32px] ring-1 ring-white/15">
        {h.image && <img src={h.image} alt="Laundry" className="h-[340px] w-full object-cover sm:h-[460px]" data-testid="hero-image" />}
        <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
      </div>
      {h.stat_value && (
        <div className="absolute -bottom-6 left-4 rounded-2xl border border-white/15 bg-navy/80 px-5 py-4 shadow-2xl backdrop-blur-xl sm:-left-6" data-testid="hero-stat">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">{h.stat_label}</p>
          <p className="mt-1 font-heading text-2xl font-extrabold">{h.stat_value}</p>
        </div>
      )}
      {h.pill_text && (
        <div className="absolute -top-4 right-4 flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-bold text-slate-900 shadow-2xl sm:-right-4" data-testid="hero-pill">
          <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-orange opacity-60" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-orange" /></span>
          {h.pill_text}
        </div>
      )}
    </motion.div>
  );
}

export function PartnersStrip({ c }) {
  const [names, setNames] = useState([]);
  useEffect(() => {
    api.get("/laundries").then((r) => setNames(r.data.map((l) => l.name))).catch(() => {});
  }, []);
  const all = [...names, ...((c.partners || {}).extra_names || [])].filter(Boolean);
  if (!all.length) return null;
  const row = all.length < 6 ? [...all, ...all, ...all] : all;
  return (
    <section className="border-t border-white/10 bg-navy py-10" data-testid="partners-strip">
      <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-white/50">{c.partners?.overline}</p>
      <div className="marquee-wrap relative mt-6 overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_10%,black_90%,transparent)]">
        <div className="marquee-track flex w-max gap-3">
          {[...row, ...row].map((n, i) => (
            <span key={i} className="flex items-center gap-2 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white/80">
              <Store className="h-4 w-4 text-brand-orange" /> {n}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowItWorks({ c }) {
  return (
    <section id="how" className="scroll-mt-24 bg-white" data-testid="how-section">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <SectionHead overline={c.how_subheading} heading={c.how_heading} />
        <div className="relative mt-14 grid gap-6 md:grid-cols-3">
          <div className="absolute left-0 right-0 top-[52px] hidden h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent md:block" />
          {(c.how_steps || []).map((s, i) => {
            const Icon = HOW_ICONS[i] || Sparkles;
            return (
              <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
                className="group relative rounded-3xl border border-slate-200 bg-white p-8 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-brand/30 hover:shadow-xl hover:shadow-brand/5" data-testid={`how-step-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand text-white shadow-lg shadow-brand/25 transition-transform group-hover:-rotate-6"><Icon className="h-6 w-6" /></span>
                  <span className="font-heading text-5xl font-extrabold text-slate-100">0{i + 1}</span>
                </div>
                <h3 className="mt-6 font-heading text-xl font-bold text-slate-900">{s.title}</h3>
                <p className="mt-2 leading-relaxed text-slate-500">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const ROLE_SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7"];

export function Roles({ c }) {
  return (
    <section id="roles" className="scroll-mt-24 bg-slate-50" data-testid="roles-section">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <SectionHead overline={c.roles_subheading} heading={c.roles_heading} />
        <div className="mt-14 grid gap-6 lg:grid-cols-12">
          {(c.roles || []).map((r, i) => <RoleCard key={i} r={r} i={i} className={ROLE_SPANS[i % 4]} />)}
        </div>
      </div>
    </section>
  );
}

function RoleCard({ r, i, className }) {
  const dark = i % 4 === 1 || i % 4 === 2;
  const Icon = ICONS[r.icon] || Store;
  return (
    <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i % 2} data-testid={`role-card-${i}`}
      className={`group relative flex flex-col overflow-hidden rounded-3xl p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-2xl ${dark ? "hero-glow text-white" : "border border-slate-200 bg-white"} ${className}`}>
      <div className="flex items-center gap-3">
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${dark ? "bg-white/10 text-brand-orange" : "bg-accent text-brand"}`}><Icon className="h-5 w-5" /></span>
        <span className={`text-xs font-bold uppercase tracking-[0.2em] ${dark ? "text-white/60" : "text-brand"}`}>{r.tag}</span>
      </div>
      <h3 className={`mt-6 font-heading text-2xl font-extrabold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>{r.title}</h3>
      <p className={`mt-3 max-w-md leading-relaxed ${dark ? "text-white/70" : "text-slate-500"}`}>{r.body}</p>
      {r.image && (
        <div className="mt-8 overflow-hidden rounded-2xl">
          <img src={r.image} alt={r.title} className="h-52 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
      )}
    </motion.div>
  );
}
